const decodeSurveyData = ({ parsedResults, parsedSurvey }) => {
    let questionComments = null;
    let surveyAnswers = null;
    let surveyQuestions = null;

    let comments = [];

    const extractElements = (elements, pageIndex) => {
        const result = [];

        for (const e of elements || []) {
            if (e.type === 'panel') {
                result.push(...extractElements(e.elements, pageIndex));
            }
            else if (e.type === 'paneldynamic') {
                result.push(...extractElements(e.templateElements, pageIndex));
            }
            else {
                result.push({ ...e, pageIndex });
            }
        }

        return result;
    };

    const questions = parsedSurvey.pages
        .flatMap((page, pageIndex) => extractElements(page.elements, pageIndex))
        .map((e, i) => {
            const titleLength = e.title ? e.title.length * 10 : 0;
            const width = titleLength > 120 ? titleLength : 120;

            const question = {
                id: i,
                field: e.name,
                headerName: e.title || e.name,
                description: e.description,
                width,
                questionType: e.type,
                pageId: e.pageIndex,
                commentText: e.commentText,
                isRequired: e.isRequired
            };

            // todo: align/headerAlign if integers

            if (e.type === 'multipletext') {
                question.items = e.items;
            }

            if (e.type === 'matrix'
                || e.type === 'matrixdropdown') {
                question.columns = e.columns;
                question.rows = e.rows;
            }

            if (e.type === 'matrixdynamic') {
                // columns contain choices, celltype, required etc
                // but they can also be defined at question level
                question.columns = e.columns;
            }

            if (e.type === 'matrixdropdown'
                || e.type === 'dropdown'
                || e.type === 'ranking'
                || e.type === 'radiogroup'
                || e.type === 'checkbox'
                || e.type === 'tagbox') {
                question.choices = e.choices;
            }

            if (e.type === 'boolean') {
                question.valueTrue = e.valueTrue;
                question.valueFalse = e.valueFalse;
                question.labelTrue = e.labelTrue;
                question.labelFalse = e.labelFalse;
            }

            if (e.type === 'rating') {
                question.rateType = e.rateType;
            }

            if (e.type === 'text') {
                question.inputType = e.inputType;
            }

            if (e.type === 'matrixdropdown') {
                question.cellType = e.cellType;
            }

            return question;
        });

    surveyQuestions = questions;

    const findAnswersByField = (result, field, questionType, rowsMap, columnsMap) => {
        let results = [];

        const search = value => {
            if (Array.isArray(value)) {
                value.forEach(item => search(item));
            }
            else if (value && typeof value === 'object') {
                if (field in value) {
                    results.push(value[field]);

                    let comment = value[`${field}-Comment`];

                    if (comment) {
                        comments.push({
                            question: field,
                            comment
                        });
                    }
                    else if (questionType === 'matrixdynamic') {
                        const mdComments = [];

                        const questionValue = value?.[field];

                        if (Array.isArray(questionValue)) {
                            questionValue.forEach(row => {
                                if (typeof row !== 'object' || row === null) {
                                    return;
                                }

                                Object.entries(row).forEach(([key, val]) => {
                                    if (key.endsWith('-Comment')) {
                                        const columnKey = key.replace('-Comment', '');
                                        const column = columnsMap?.find(e => e.name === columnKey);
                                        const columnName = column?.title || column?.name || columnKey;

                                        mdComments.push({
                                            question: field,
                                            column: columnName,
                                            comment: val
                                        });
                                    }
                                });
                            });
                        }

                        if (mdComments.length > 0) {
                            comments = [...comments, ...mdComments];
                        }
                    }
                    else if (questionType === 'matrixdropdown') {
                        const mddComments = [];

                        const questionValue = value?.[field];

                        if (questionValue && typeof questionValue === 'object') {
                            Object.entries(questionValue).forEach(([row, columns]) => {
                                Object.entries(columns)
                                    .filter(([key]) => key.endsWith('-Comment'))
                                    .forEach(([key, comment]) => {
                                        const rowName = rowsMap?.find(e => e.value === row)?.text;
                                        const columnKey = key.replace('-Comment', '');
                                        const column = columnsMap?.find(e => e.name === columnKey);
                                        const columnName = column?.title || column?.name || columnKey;

                                        mddComments.push({
                                            question: field,
                                            row: rowName ?? row,
                                            column: columnName,
                                            comment
                                        });
                                    });
                            });
                        }

                        if (mddComments) {
                            comments = [...comments, ...mddComments];
                        }
                    }
                }

                Object.values(value).forEach(subValue => search(subValue));
            }
        };

        search(result);

        return results;
    };

    const convertMatrixDropdownAnswers = (question, answers) => {
        const columns = question.columns.map(c => {
            const cellType = c.cellType || question.cellType || 'dropdown';
            const choices = cellType === 'rating' ? c.rateValues : c.choices || question.choices;

            return {
                ...c,
                cellType,
                choices
            };
        });

        const rowMap = Object.fromEntries(question.rows.map(r => [r.value, r.text]));
        const columnMap = Object.fromEntries(columns.map(c => [c.name, c.title]));

        const choiceMaps = Object.fromEntries(
            columns.map(c => [
                c.name,
                Object.fromEntries(c.choices.map(choice => {
                    if (typeof choice === 'string') {
                        // if celltype rating or in unlikely case of leaving the choices as 1-5 (defaults), choices will be array of strings not objects
                        return [choice, choice];
                    }

                    return [choice.value, choice.text];
                }))
            ])
        );

        return answers?.flatMap(entry =>
            Object.entries(entry).flatMap(([rowKey, cols]) => {
                const expanded = Object.entries(cols).flatMap(([colKey, value]) => {
                    if (Array.isArray(value)) {
                        // if multi-select celltype (check/tagboxes) then value can be an array
                        return value.map(v => [colKey, v]);
                    }

                    return [[colKey, value]];
                });

                return expanded.map(([colKey, singleValue]) => ({
                    [rowMap[rowKey]]: {
                        [columnMap[colKey]]: choiceMaps[colKey]?.[singleValue] ?? singleValue
                    }
                }));
            }) ?? []
        );
    };

    const convertColumnRowAnswers = (rows, columns, answers) => {
        const rowMap = rows.reduce((acc, { value, text }) => ({ ...acc, [value]: text }), {});
        const colMap = columns.reduce((acc, { value, text }) => ({ ...acc, [value]: text }), {});

        return answers.map(answer => Object.entries(answer).reduce((acc, [rowKey, colVal]) => ({
            ...acc, [rowMap[rowKey] || rowKey]: colMap[colVal] || colVal
        }), {}));
    };

    const convertChoices = (choices, answers) => answers.flat().map(e => choices.find(c => c.value === e)?.text ?? e);

    const convertMatrixDynamicAnswers = (columns, answers) => {
        const columnMap = columns.reduce((acc, col) => {
            acc[col.name] = {
                title: col.title || col.name,
                valueMap: col.choices?.reduce((map, choice) => {
                    map[choice.value] = choice.text;

                    return map;
                }, {}) || {}
            };

            return acc;
        }, {});

        const flatData = answers.flat();

        return flatData.map(row =>
            Object.entries(row).reduce((acc, [colName, value]) => {
                const { title, valueMap } = columnMap[colName] || {};

                if (colName.endsWith('-Comment') || !title) {
                    return acc;
                }

                // eslint-disable-next-line no-prototype-builtins
                acc[title] = valueMap?.hasOwnProperty(value) ? valueMap[value] : value;

                return acc;
            }, {})
        );
    };

    const convertBooleanLabels = (labelTrue, labelFalse, valueTrue, valueFalse, answers) => {
        const trueString = valueTrue || 'true';
        const falseString = valueFalse || 'false';

        return answers.map(e => {
            if (labelTrue && e === trueString) {
                return labelTrue;
            }

            if (labelFalse && e === falseString) {
                return labelFalse;
            }

            return e ? 'True' : 'False';
        });
    };

    const answers = parsedResults.map((result, i) => questions.reduce((acc, q) => {
        let values = findAnswersByField(result, q.field, q.questionType, q.rows, q.columns);

        switch (q.questionType) {
            case 'matrix':
                values = convertColumnRowAnswers(q.rows, q.columns, values);
                acc[q.field] = values.length === 0 ? null : JSON.stringify(values);

                break;

            case 'matrixdynamic':
                values = convertMatrixDynamicAnswers(q.columns, values);
                acc[q.field] = values.length === 0 ? null : JSON.stringify(values);

                break;

            case 'matrixdropdown':
                values = convertMatrixDropdownAnswers(q, values);
                acc[q.field] = values.length === 0 ? null : JSON.stringify(values);

                break;

            case 'boolean':
                values = convertBooleanLabels(q.labelTrue, q.labelFalse, q.valueTrue, q.valueFalse, values);
                acc[q.field] = values.length === 0 ? null : values.length === 1 ? values[0] : values;

                break;

            case 'multipletext':
                values = convertChoices(q.items, values);
                acc[q.field] = values.length === 0 ? null : JSON.stringify(values);

                break;

            case 'radiogroup':
            case 'checkbox':
            case 'tagbox':
            case 'dropdown':
            case 'ranking':
                values = convertChoices(q.choices, values);
                acc[q.field] = values.length === 0 ? null : values.length === 1 ? values[0] : values;

                break;

            default:
                acc[q.field] = values.length === 0 ? null : values.length === 1 ? values[0] : values;
        }

        return acc;
    }, { id: i }));

    surveyAnswers = answers;
    questionComments = comments;

    return {
        answers: surveyAnswers,
        questions: surveyQuestions,
        questionComments
    };

};

export default decodeSurveyData;